import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { tap } from 'rxjs/operators';
import { EnvService } from 'src/app/env.service';

@Injectable({
  providedIn: 'root',
})
export class BillingUsageService {
  private http = inject(HttpClient);
  creditAlertTriggered = signal<boolean>(false);
  lockedFeatureAlertTriggered = signal<boolean>(false);
  aiRestrictedAlertTriggered = signal<boolean>(false);

  // Shared global signals representing current credit status across all components
  creditTotal = signal<number>(0);
  creditUsage = signal<number>(0);

  // Timestamp to throttle manual/automatic requests and avoid server spam
  private lastFetchTime = 0;
  private env = inject(EnvService);
  private translate = inject(TranslateService);

  private get baseUrl() {
    return this.env.apiUrl;
  }

  private get locale(): string {
    return this.translate.currentLang || 'fr';
  }

  getBillingUsage(
    pageSize: number,
    pageIndex: number,
    sortField?: string,
    sortDirection?: string,
    filters?: { startDate?: string; endDate?: string; type?: string; userId?: string }
  ) {
    let url = this.baseUrl + `billing/usage?pageSize=${pageSize}&pageIndex=${pageIndex}`;
    if (sortField?.length && sortDirection) {
      url += `&sortField=${sortField}&sortDirection=${sortDirection}`;
    }
    if (filters?.startDate) url += `&startDate=${filters.startDate}`;
    if (filters?.endDate) url += `&endDate=${filters.endDate}`;
    if (filters?.type) url += `&type=${filters.type}`;
    if (filters?.userId) url += `&userId=${filters.userId}`;
    return this.http.get<{ data: any[]; total: number }>(url);
  }

  getAvailableCredits() {
    return this.http
      .get<{
        globalTotal: number;
        creditTotal: number;
        creditUsage: number;
        projects: any[];
        subscriptionData?: any;
        trialEnd?: number | null;
        planPrice?: number | null;
        planCurrency?: string | null;
        planInterval?: string | null;
      }>(this.baseUrl + 'billing/credits')
      .pipe(
        tap((res) => {
          if (res) {
            // Keep our global signals synchronized automatically with every fetch
            this.creditTotal.set(res.creditTotal ?? 0);
            this.creditUsage.set(res.creditUsage ?? 0);
          }
        })
      );
  }

  /**
   * Refreshes the shared credit signals in the background.
   * To prevent spamming the backend with excessive queries when multiple requests are triggered consecutively,
   * a 30-second debounce/cooldown mechanism is enforced.
   *
   * @param force Set to true to bypass the cooldown (e.g. for initial load).
   */
  refreshAvailableCredits(force = false) {
    const now = Date.now();
    const cooldown = 30000; // 30 seconds cooldown interval

    if (!force && now - this.lastFetchTime < cooldown) {
      // TODO: Future optimization - Instead of silently dropping all requests during the cooldown,
      // we could schedule a single deferred refresh at the end of the 30-second window (using a setTimeout or RxJS timer)
      // to guarantee that the final credit count updates correctly after a sequence of operations completes.
      return;
    }

    this.lastFetchTime = now;

    // This background call updates the global signals (creditTotal and creditUsage) via the tap pipe.
    this.getAvailableCredits().subscribe({
      error: (err) => console.error('Failed to auto-refresh credits in background', err),
    });
  }

  getBillingStats() {
    return this.http.get<{
      trendData: number[];
      topConsumers: { id: string; name: string; avatar: string; credits: number }[];
      featureBreakdown: { type: string; credits: number }[];
      periodStart: string;
      periodEnd: string;
    }>(this.baseUrl + 'billing/stats');
  }

  createCheckoutSession() {
    return this.http.post<{ url: string }>(this.baseUrl + 'billing/checkout', { locale: this.locale });
  }

  createPortalSession() {
    return this.http.post<{ url: string }>(this.baseUrl + 'billing/portal', { locale: this.locale });
  }

  createPaymentMethodSession() {
    return this.http.post<{ url: string }>(this.baseUrl + 'billing/add-payment-method', { locale: this.locale });
  }

  convertTrial() {
    return this.http.post<{ converted: boolean; url?: string }>(this.baseUrl + 'billing/convert-trial', { locale: this.locale });
  }

  toggleOverage(enabled: boolean) {
    return this.http.post<{ success: boolean; overageEnabled: boolean }>(this.baseUrl + 'billing/overage/toggle', { enabled });
  }
}
